import React, { useEffect, useState } from "react";
import { fetchDailyPlannings } from "../api";
import { Table, TableBody, TableCell, TableHead, TableRow, Button } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import getWorkcellColor from "../utils/getWorkcellColor";
import { useTranslation } from "react-i18next";
import { useCustomerCurrency, useCustomerLocale } from "../context/CustomerSettingsContext";

const DailyPlanningsTable = ({ date, onSelectWorkcell, onLoadingChange }) => {
  const [data, setData] = useState([]);
  const theme = useTheme();
  const { t } = useTranslation();
  const locale = useCustomerLocale();
  const { formatCurrency } = useCustomerCurrency();

  const formatCount = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return t("common.na");
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 })
      .formatToParts(num)
      .map((part) => (part.type === "group" ? "," : part.value))
      .join("");
  };

  useEffect(() => {
    if (!date) {
      onLoadingChange?.(false);
      return;
    }
    onLoadingChange?.(true);
    fetchDailyPlannings(date)
      .then(apiData => {
        setData(apiData || []);
      })
      .catch(error => {
        console.error('Error fetching plannings data:', error);
        setData([]);
      })
      .finally(() => {
        onLoadingChange?.(false);
      });
  }, [date]);

  // Compute sorted list of all workcell IDs for consistent color mapping
  const allWorkcellIds = data && data.length
    ? [...new Set(data.map(row => row.workcell_id))].sort((a, b) => a - b)
    : [];

  return (
    <Table sx={{ tableLayout: 'fixed' }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: 'auto' }}>{t("daily.plannings.table.workcell")}</TableCell>
          <TableCell align="right" sx={{ width: '10rem' }}>{t("daily.plannings.table.operations")}</TableCell>
          <TableCell align="right" sx={{ width: '10rem' }}>{t("daily.plannings.table.operationRate")}</TableCell>
          <TableCell align="right" sx={{ width: '10rem' }}>{t("daily.plannings.table.internalTurnover")}</TableCell>
          <TableCell sx={{ width: '10rem' }} />
        </TableRow>
      </TableHead>
      <TableBody>
        {data
          .filter(row => row.total_completed > 0 && row.workcell_id !== "Total")
          .map((row) => (
            <TableRow key={row.workcell_id}>
              <TableCell
                sx={{
                  color: getWorkcellColor(row.workcell_id, theme.palette.chart.colors, allWorkcellIds),
                  fontWeight: 'bold'
                }}
              >
                {row.workcell_name}
              </TableCell>
              <TableCell align="right">{formatCount(row.total_completed)}</TableCell>
              <TableCell align="right">{formatCurrency(row.operation_rate)}</TableCell>
              <TableCell align="right">{formatCurrency(row.internal_turnover)}</TableCell>
              <TableCell align="right">
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => onSelectWorkcell(row.workcell_id)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t("common.viewDetails")}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        {/* Totals row */}
        {data.length > 0 && data[data.length - 1].workcell_id === "Total" && (
          <TableRow sx={{ fontWeight: 'bold', backgroundColor: theme.palette.action.hover }}>
            <TableCell component="th" scope="row">{t("common.total")}</TableCell>
            <TableCell align="right">{formatCount(data[data.length - 1].total_completed)}</TableCell>
            <TableCell />
            <TableCell align="right">{formatCurrency(data[data.length - 1].internal_turnover)}</TableCell>
            <TableCell />
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export default DailyPlanningsTable;
